'use client';

import { clearAuthToken, getAuthToken, isTokenExpired, refreshSession, setMfaApiFetch } from '@tracedocuments/auth';

import { createCorrelationId, getCorrelationIdFromResponse, withCorrelationHeaders } from './correlation';
import { emitApiRequestPerformance, toApiRouteFamily } from './performance-telemetry';
import {
    ApiRequestTimeoutError,
    createRequestSignal,
    DEFAULT_API_TIMEOUT_MS,
} from './request-timeout';

/** Response shape for incremental "what changed since X" list endpoints. */
export type ChangedSinceResult<T> = {
    items: T[];
    serverTime: string;
};

export class ApiError extends Error {
    status: number;
    correlationId?: string;

    constructor(message: string, status: number, correlationId?: string) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.correlationId = correlationId;
    }
}

export class AuthSessionError extends Error {
    constructor(message = 'Session expired') {
        super(message);
        this.name = 'AuthSessionError';
    }
}

// ---------------------------------------------------------------------------
// Configurable API base URL
// ---------------------------------------------------------------------------

let _apiUrl: string | null = null;
let _nativeClientHeader: string | null = null;
let _clientPlatformHeader: string | null = null;

export const CLIENT_PLATFORM_HEADER = 'X-TraceDocuments-Client-Platform';

export type ClientPlatform =
    | 'WEB'
    | 'ADMIN_WEB'
    | 'ANDROID'
    | 'IOS'
    | 'ADMIN_ANDROID'
    | 'ADMIN_IOS'
    | 'DESKTOP_WINDOWS';

export function setApiBaseUrl(url: string): void {
    _apiUrl = url;
}

/** Mobile native app sets this so protected shares can stream from the API. */
export function setNativeClientHeader(value: string | null): void {
    _nativeClientHeader = value;
}

/** Explicit app platform for access/session analytics (WEB, ANDROID, IOS, …). */
export function setClientPlatformHeader(value: ClientPlatform | string | null): void {
    _clientPlatformHeader = value;
}

export function getClientPlatformHeader(): string | null {
    return _clientPlatformHeader;
}

function applyClientIdentityHeaders(headers: Headers): void {
    if (_nativeClientHeader) {
        headers.set('X-TraceDocuments-Client', _nativeClientHeader);
    }
    if (_clientPlatformHeader) {
        headers.set(CLIENT_PLATFORM_HEADER, _clientPlatformHeader);
    }
}

export function getApiBaseUrl(): string {
    // Test escape-hatch: E2E tests inject `window.__API_BASE_URL_OVERRIDE__`
    // via an init script to force same-origin fetches. This avoids fighting
    // cross-origin CORS for `route.fulfill` mocks in production smoke suites.
    // The flag is undefined in real production builds, so this is a no-op
    // for end users.
    if (typeof window !== 'undefined') {
        const override = (window as { __API_BASE_URL_OVERRIDE__?: string }).__API_BASE_URL_OVERRIDE__;
        if (typeof override === 'string') return override;
    }
    return _apiUrl ?? process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:5090';
}

// ---------------------------------------------------------------------------
// Core authenticated fetch
// ---------------------------------------------------------------------------

export type ApiFetchInit = RequestInit & {
    /** When false, 401 is returned to the caller (e.g. wrong password) instead of refresh+retry. Default true. */
    retryOn401?: boolean;
    /** Per-attempt timeout. Use false only when a caller owns a stricter timeout. */
    timeoutMs?: number | false;
};

export async function apiFetch(path: string, init?: ApiFetchInit): Promise<Response> {
    const { retryOn401 = true, timeoutMs = DEFAULT_API_TIMEOUT_MS, ...fetchInit } = init ?? {};
    const effectiveTimeoutMs = timeoutMs === false ? null : timeoutMs;
    const correlationId = createCorrelationId();

    const callApi = async (token: string | null, attempt: number) => {
        const headers = withCorrelationHeaders(fetchInit.headers, correlationId);
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        applyClientIdentityHeaders(headers);

        const requestSignal = effectiveTimeoutMs === null
            ? null
            : createRequestSignal(fetchInit.signal, effectiveTimeoutMs);
        const startedAt = Date.now();

        try {
            const response = await fetch(`${getApiBaseUrl()}${path}`, {
                ...fetchInit,
                headers,
                signal: requestSignal?.signal ?? fetchInit.signal,
            });
            emitApiRequestPerformance({
                kind: 'api_request',
                requestKind: 'authenticated',
                method: (fetchInit.method ?? 'GET').toUpperCase(),
                routeFamily: toApiRouteFamily(path),
                platform: _clientPlatformHeader ?? 'UNKNOWN',
                durationMs: Date.now() - startedAt,
                outcome: response.ok ? 'success' : 'http_error',
                attempt,
                statusCode: response.status,
                correlationId: getCorrelationIdFromResponse(response) ?? correlationId,
            });
            return response;
        } catch (err) {
            if (requestSignal?.didTimeout() && err instanceof Error && err.name === 'AbortError') {
                emitApiRequestPerformance({
                    kind: 'api_request',
                    requestKind: 'authenticated',
                    method: (fetchInit.method ?? 'GET').toUpperCase(),
                    routeFamily: toApiRouteFamily(path),
                    platform: _clientPlatformHeader ?? 'UNKNOWN',
                    durationMs: Date.now() - startedAt,
                    outcome: 'timeout',
                    attempt,
                    correlationId,
                });
                throw new ApiRequestTimeoutError(path, effectiveTimeoutMs!);
            }
            emitApiRequestPerformance({
                kind: 'api_request',
                requestKind: 'authenticated',
                method: (fetchInit.method ?? 'GET').toUpperCase(),
                routeFamily: toApiRouteFamily(path),
                platform: _clientPlatformHeader ?? 'UNKNOWN',
                durationMs: Date.now() - startedAt,
                outcome: fetchInit.signal?.aborted ? 'cancelled' : 'network_error',
                attempt,
                correlationId,
            });
            throw err;
        } finally {
            requestSignal?.dispose();
        }
    };

    let currentToken = getAuthToken();

    if (currentToken && isTokenExpired(currentToken)) {
        const refreshed = await refreshSession();
        if (!refreshed) {
            clearAuthToken('expired-token');
            throw new AuthSessionError('Session expired');
        }

        currentToken = refreshed;
    }

    let response = await callApi(currentToken, 1);

    if (response.status === 401 && !retryOn401) {
        return response;
    }

    if (response.status === 401) {
        const refreshedAccessToken = await refreshSession();
        if (!refreshedAccessToken) {
            clearAuthToken('unauthorized');
            throw new AuthSessionError('Unauthorized');
        }

        response = await callApi(refreshedAccessToken, 2);
        if (response.status === 401) {
            clearAuthToken('unauthorized');
            throw new AuthSessionError('Unauthorized');
        }
    }

    return response;
}

/** Parse JSON error body; surfaces correlationId from API when present. */
export async function parseApiErrorResponse(response: Response): Promise<ApiError> {
    const headerCorrelation = getCorrelationIdFromResponse(response);
    let message = response.statusText || `HTTP ${response.status}`;
    let bodyCorrelation: string | undefined;

    try {
        const body = (await response.json()) as {
            error?: string;
            message?: string;
            title?: string;
            detail?: string;
            correlationId?: string;
        };
        message = body.error ?? body.message ?? body.detail ?? body.title ?? message;
        bodyCorrelation = body.correlationId;
    } catch {
        // ignore non-JSON bodies
    }

    return new ApiError(message, response.status, headerCorrelation ?? bodyCorrelation);
}

// ---------------------------------------------------------------------------
// Multipart upload with progress (XMLHttpRequest — fetch() exposes no upload
// progress events on either web or React Native).
// ---------------------------------------------------------------------------

export type ApiUploadInit = {
    method?: 'POST' | 'PUT' | 'PATCH';
    /** 0–100, reported as the request body is transferred to the server. */
    onProgress?: (percent: number) => void;
    signal?: AbortSignal;
};

function xhrRequest(
    method: string,
    url: string,
    formData: FormData,
    token: string | null,
    correlationId: string,
    init?: ApiUploadInit,
): Promise<{ status: number; body: string }> {
    return new Promise((resolve, reject) => {
        if (init?.signal?.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
            return;
        }

        const xhr = new XMLHttpRequest();
        xhr.open(method, url);
        xhr.setRequestHeader('X-Correlation-Id', correlationId);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        if (_nativeClientHeader) xhr.setRequestHeader('X-TraceDocuments-Client', _nativeClientHeader);
        if (_clientPlatformHeader) xhr.setRequestHeader(CLIENT_PLATFORM_HEADER, _clientPlatformHeader);

        const onAbort = () => xhr.abort();
        init?.signal?.addEventListener('abort', onAbort);
        const cleanup = () => init?.signal?.removeEventListener('abort', onAbort);

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && init?.onProgress) {
                init.onProgress(Math.round((event.loaded / event.total) * 100));
            }
        };
        xhr.onabort = () => {
            cleanup();
            reject(new DOMException('Aborted', 'AbortError'));
        };
        xhr.onerror = () => {
            cleanup();
            reject(new TypeError('Network request failed'));
        };
        xhr.onload = () => {
            cleanup();
            resolve({ status: xhr.status, body: xhr.responseText });
        };
        xhr.send(formData);
    });
}

/** Authenticated multipart upload with progress + cancellation. Mirrors apiFetch's 401-refresh-retry. */
export async function apiUpload(
    path: string,
    formData: FormData,
    init?: ApiUploadInit,
): Promise<Response> {
    const method = init?.method ?? 'POST';
    const correlationId = createCorrelationId();
    const url = `${getApiBaseUrl()}${path}`;

    let currentToken = getAuthToken();
    if (currentToken && isTokenExpired(currentToken)) {
        const refreshed = await refreshSession();
        if (!refreshed) {
            clearAuthToken('expired-token');
            throw new AuthSessionError('Session expired');
        }
        currentToken = refreshed;
    }

    let result = await xhrRequest(method, url, formData, currentToken, correlationId, init);

    if (result.status === 401) {
        const refreshedAccessToken = await refreshSession();
        if (!refreshedAccessToken) {
            clearAuthToken('unauthorized');
            throw new AuthSessionError('Unauthorized');
        }
        result = await xhrRequest(method, url, formData, refreshedAccessToken, correlationId, init);
        if (result.status === 401) {
            clearAuthToken('unauthorized');
            throw new AuthSessionError('Unauthorized');
        }
    }

    return new Response(result.body, { status: result.status });
}

/** Unauthenticated API call with correlation header (login, public endpoints). */
export type PublicApiFetchInit = RequestInit & {
    /** Per-request timeout. Use false for intentionally long-lived requests. */
    timeoutMs?: number | false;
};

export async function publicApiFetch(path: string, init?: PublicApiFetchInit): Promise<Response> {
    const { timeoutMs = DEFAULT_API_TIMEOUT_MS, ...fetchInit } = init ?? {};
    const effectiveTimeoutMs = timeoutMs === false ? null : timeoutMs;
    const correlationId = createCorrelationId();
    const headers = withCorrelationHeaders(fetchInit.headers, correlationId);
    applyClientIdentityHeaders(headers);
    const requestSignal = effectiveTimeoutMs === null
        ? null
        : createRequestSignal(fetchInit.signal, effectiveTimeoutMs);
    const startedAt = Date.now();

    try {
        const response = await fetch(`${getApiBaseUrl()}${path}`, {
            ...fetchInit,
            headers,
            signal: requestSignal?.signal ?? fetchInit.signal,
        });
        emitApiRequestPerformance({
            kind: 'api_request',
            requestKind: 'public',
            method: (fetchInit.method ?? 'GET').toUpperCase(),
            routeFamily: toApiRouteFamily(path),
            platform: _clientPlatformHeader ?? 'UNKNOWN',
            durationMs: Date.now() - startedAt,
            outcome: response.ok ? 'success' : 'http_error',
            attempt: 1,
            statusCode: response.status,
            correlationId: getCorrelationIdFromResponse(response) ?? correlationId,
        });
        return response;
    } catch (err) {
        if (requestSignal?.didTimeout() && err instanceof Error && err.name === 'AbortError') {
            emitApiRequestPerformance({
                kind: 'api_request',
                requestKind: 'public',
                method: (fetchInit.method ?? 'GET').toUpperCase(),
                routeFamily: toApiRouteFamily(path),
                platform: _clientPlatformHeader ?? 'UNKNOWN',
                durationMs: Date.now() - startedAt,
                outcome: 'timeout',
                attempt: 1,
                correlationId,
            });
            throw new ApiRequestTimeoutError(path, effectiveTimeoutMs!);
        }
        emitApiRequestPerformance({
            kind: 'api_request',
            requestKind: 'public',
            method: (fetchInit.method ?? 'GET').toUpperCase(),
            routeFamily: toApiRouteFamily(path),
            platform: _clientPlatformHeader ?? 'UNKNOWN',
            durationMs: Date.now() - startedAt,
            outcome: fetchInit.signal?.aborted ? 'cancelled' : 'network_error',
            attempt: 1,
            correlationId,
        });
        throw err;
    } finally {
        requestSignal?.dispose();
    }
}

// Wire up MFA to use apiFetch for authenticated MFA endpoints
setMfaApiFetch(apiFetch);
